import React, { useState, useRef, useMemo } from "react";
import { Button, Input, InputRef, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { IComponentField } from '../../../core/types/IComponentField';
import { ComponentsHelper } from "../../../core/services/ComponentHelper";
import IconTextButton from '../../../repertoire/components/iconTextButton/IconTextButton';
import ShowMore from '../../../repertoire/components/showMore/ShowMore';
import { EXTERNAL_SVG, TRIGGER_AUTO_ICON, DELETE_ICON } from '../../../repertoire/IconConsts';
import { ColumnType, FilterConfirmProps } from "antd/lib/table/interface";
import { CONFIRM_UNDO_PROMPT_STATE_KEY } from '../../Consts';
import { IStoragePathItemData } from "../../types/IStoragePathItemData";
import { IComponentDataItem } from "../../../core/types/IComponentDataItem";
import { IDistribution } from "../../../repertoire/types/usageTypes/IDistribution";
import { ILookupInstance } from "../../../lookup/types/ILookup";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { ITreeData } from "../../../repertoire/types/ITreeData";
import { IFileSystem } from "../../types/IFileSystem";
import { IUsagePool } from "../../../repertoire/types/usageTypes/IUsagePool";
import { IJobState } from '../../../redux/types/IJobState';
import Highlighter from 'react-highlight-words';
import { editingChanged, fileMetadataChanged } from '../../../redux/reducers/DataIngestionReducer';
import { IYesNoPromptViewModalProps } from "../../../repertoire/components/modalViews/yesNoPromptView/YesNoPromptView";
import { hideModal, showYesNoPrompt } from '../../../redux/reducers/ModalReducer';
import DropdownDataInput from '../../../repertoire/components/dropdownDataInput/DropdownDataInput';
import DropdownTreeDataInput from "../../../repertoire/components/dropdownTreeDataInput/DropdownTreeDataInput";
import { ATTACHMENT_TYPE_TABLE_HEADER_FIELD, REMOVE } from "../containerDetailsWindow/ContainerDetailsWindowFields";
import { ITabReduxItem } from "../../../redux/types/ITabReduxItem";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import { SEARCH_VIEW_LICENSES_FILEIMPORT } from "../../../repertoire/Consts";

interface IDataIngestionTableProps {
    containerDetailsWindowComponentData: IComponentDataItem;
    storagePaths: IStoragePathItemData[];
    storagePathItemData: IComponentDataItem;
    distributions: IDistribution[];
    destinations: ILookupInstance[];
    matchingSources: ITreeData[];
    formats: ILookupInstance[];
    sources: ITreeData[];
    titleContains: string;
    distributionsFilter: string[];
    selectedFileSystem: IFileSystem;
    isRepertoireModule: boolean;
    pools: IUsagePool[];
    jobs: IJobState[];
    canEditMetadata: boolean;
    displayAttachmentType: boolean;
    displayRemoveColumn: boolean;
    attchmentTypeLookupOptions: ILookupInstance[];
    activeTab: number;
    tabs: ITabReduxItem[];
    determineIsReadOnly: (storagePathFullName: string) => boolean;
    deleteFile: (fullName: string, name: string) => void;
    editingChanged: typeof editingChanged;
    matchNow: (data: IStoragePathItemData) => void;
    undoUsageIngestion?: (fullFilePath: string) => void;
    showYesNoPrompt: typeof showYesNoPrompt;
    hideModal: typeof hideModal;
    fileMetadataChanged: typeof fileMetadataChanged;
    getDistributionDetails: (distributionID: number, lookupValues: ILookupDictionary) => void;
    getUsagePool: (poolId: number, matchingSources: ITreeData[], isPoolMaintainence?: boolean) => void;
    openDirectory: (filesystem: IFileSystem, sources: ITreeData[]) => void;
    showSearchModal: (modalProps: any, searchViewKey: string) => void;
    removeAttachmentFromMaintenanceScreen: (fullName: string, guid: string, index: number) => void;
    onDownloadFile: (data: IStoragePathItemData, isRepertoireModule: boolean) => void;
}
interface IDataIngestionTableRow {
    key: string;
    FilenameTableHeader?: string;
    distributionCode?: string;
    poolCode?: string;
    format?: string;
    outputFormat?: string;
    source?: string;
    destination?: string;
    fileSize?: number;
    LastModifiedTableHeader?: string;
    TrackingInformationTableHeader?: string;
    inProgress?: boolean;
    isEditing?: boolean;
    isDirectory?: boolean;
    isDirty?: boolean;
    attachmentTypeCode?: string;
    attachmentTypeID?: number;
    rightHolder?: number;
    batchID?: number;   
    validationError?: string;
}

const DataIngestionTable: React.FC<IDataIngestionTableProps> = (props) => {

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    function renderTextInputWithShowMore(record, field, showSearchModal, label, modalKey, onChange) {
        return (
            <>
                <TextDataInput
                    value={record?.[field.name] || ''}
                    changeData={(value) => onChange(value, field.name, record?.key, true)}
                    readOnly={false}
                    isHidden={false}
                    fieldName={field.name}
                />
                <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <ShowMore
                        options={[{
                            text: label,
                            onClick: () => showSearchModal(record, modalKey),
                            icon: EXTERNAL_SVG,
                        }]}
                    />
                </span>
            </>
        );
    }

    function formatFileSize(bytes: number, decimals = 2) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    function renderDropdownInput(options, value, fieldName, record, onChange, index) {
        return (
            <DropdownDataInput
                options={options}
                changeData={(val, field) => onChange(val, field, record.key, true)}
                value={value}
                fieldName={fieldName}
                allowNull={true}
                isHidden={false}
                index={index}
                useComboBoxAsMenuWidth={false}
            />
        );
    }

    const getActionIconsColumn = () => ({
        title: '',
        dataIndex: 'actionIcons',
        key: 'actionIcons',
        width: '2em',
        render: (_text: string, record: IDataIngestionTableRow) => {
            const dir: string = props.selectedFileSystem?.fullName ?? '';
            const parts = dir ? dir.split('/') : [];
            const showUndoIngestion =
                parts[0] === 'usage' &&
                (
                    (parts[3] === 'archive') ||
                    (parts[1] === 'society' && parts[2] === 'archive')
                );

            return (
                <span style={{ display: 'flex', gap: 4 }}>
                    {showUndoIngestion && (
                        <IconTextButton
                            key="undo"
                            icon="icon iconHover node-icon ms-Icon ms-Icon--Undo"
                            onClick={() => onUndoIngestion(record)}
                        />
                    )}
                    {!record.isDirectory ? (
                        <IconTextButton
                            key="download"
                            icon="icon iconHover node-icon ms-Icon ms-Icon--Download"
                            onClick={() => onDownloadFile(record)}
                        />
                    ) : (
                        <IconTextButton
                            key="download"
                            icon="icon node-icon ms-Icon ms-Icon--FabricFolder"
                        />
                    )}
                </span>
            );
        }
    });

    const getDynamicActionColumn = () => ({
        title: '',
        dataIndex: 'dynamicAction',
        key: 'dynamicAction',
        width: '1em',
        render: (_text: string, record: IDataIngestionTableRow) => {
            const canEditMetadata = props.canEditMetadata;
            const isSyncInput = record?.key === "ingestionMatchingData/sync/input";
            const isReadOnly = props.determineIsReadOnly(record?.key);
            const isEditing = record?.isEditing;
            if (record?.inProgress) {
                return (
                    <IconTextButton
                        icon="icon ms-Icon ms-Icon--SyncOccurence"
                    />
                );
            }
            else if (!isEditing && canEditMetadata && !isSyncInput) {
                return (
                    <ShowMore
                        data-testid="show-more"
                        options={[
                            {
                                text: "Edit",
                                onClick: () => { editingChanged(record) },
                                icon: EXTERNAL_SVG,
                            },
                            {
                                text: "Process Now",
                                onClick: () => { matchNow(record) },
                                icon: TRIGGER_AUTO_ICON,
                                isIcon: true
                            },
                            {
                                text: "Delete",
                                onClick: () => { onDeleteFile(record) },
                                icon: DELETE_ICON,
                                isIcon: true
                            }
                        ]}
                        isReadOnly={isReadOnly}
                    />
                );
            }
            return {};
        }
    });

    const getColumnSearchProps = (dataIndex: string, name: string): ColumnType<IDataIngestionTableRow> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                   placeholder={`Search ${name}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined translate={undefined} onAuxClick={undefined} onAuxClickCapture={undefined} />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} translate={undefined} onAuxClick={undefined} onAuxClickCapture={undefined} />
        ),
        onFilter: (value, record) => {
            return (record[dataIndex] ?? '')
                .toString().toLowerCase()
                .includes((value as string).toLowerCase())
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#fcfa5d', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: string,
    ) => {
        confirm();
        setSearchText(selectedKeys[0] ?? '');
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const onDownloadFile = (record: IDataIngestionTableRow ) => {
        const { onDownloadFile, isRepertoireModule, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            onDownloadFile(storageItem, isRepertoireModule);
        }
    };

    const onRemoveFile = (record: IDataIngestionTableRow) => {
        const { isRepertoireModule, removeAttachmentFromMaintenanceScreen } = props;
        const index = data.findIndex((item) => item.key === record.key);
        if (!record.isDirectory && isRepertoireModule) {
            const splitName: string[] = (record.key ?? '').split('/');
            removeAttachmentFromMaintenanceScreen(record.key, splitName[splitName.length - 1], index);
        }
    }

    const editingChanged = (record: IDataIngestionTableRow) => {
        const { editingChanged, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            editingChanged(storageItem);
        }
    }

    const onDeleteFile = (record: IDataIngestionTableRow) => {
        const { deleteFile } = props;
        deleteFile?.(record?.key ?? '', record?.FilenameTableHeader ?? '');
    }

    const matchNow = (record: IDataIngestionTableRow) => {
        const { matchNow, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            matchNow(storageItem);
        }
    }

    const onUndoIngestion = (record: IDataIngestionTableRow) => {
        const { undoUsageIngestion, storagePathItemData, showYesNoPrompt, hideModal, storagePaths } = props;
        
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        const fullFilePath = `${storageItem.path}/${storageItem.name}`;

        if (!record.isDirectory && !record.inProgress && !record.isDirty && !record.isEditing) {
            if (record.key.split('/')[0] === 'usage') {
                const title: string = storagePathItemData.fields.find(f => f.name === CONFIRM_UNDO_PROMPT_STATE_KEY)?.data ?? '';
                const props: IYesNoPromptViewModalProps = {
                    yesCallback: () => {
                        undoUsageIngestion(fullFilePath);
                        hideModal();
                    },
                    noCallback: () => {
                        hideModal();
                    }
                }
                showYesNoPrompt(title, props);
            }
        }
    }

    function getFilteredData(row, pools, sources, distributions) {
        let sourceValue = row?.metadata?.source ?? row?.source;
        let poolCodeValue = row?.metadata?.poolCode ?? row?.poolCode;

        if (sourceValue && sources && sources.length > 0) {
            const foundById = sources.find(x => x.id === sourceValue || x.id?.toString() === sourceValue?.toString());
            if (foundById) {
                sourceValue = foundById.hierarchy;
            }
        }

        if (poolCodeValue && pools && pools.length > 0) {
            if (typeof poolCodeValue === 'object' && poolCodeValue !== null) {
                if (poolCodeValue.poolCode) {
                    poolCodeValue = poolCodeValue.poolCode;
                } else if (poolCodeValue.id) {
                    const foundPool = pools.find(x => x.id === poolCodeValue.id || x.id?.toString() === poolCodeValue.id?.toString());
                    if (foundPool) {
                        poolCodeValue = foundPool.poolCode;
                    }
                }
            } else {
                const foundPool = pools.find(x => x.poolCode === poolCodeValue || x.id === poolCodeValue || x.id?.toString() === poolCodeValue?.toString());
                if (foundPool) {
                    poolCodeValue = foundPool.poolCode;
                }
            }
        }

        let parentNode;
        let parentPools = [];
        let childPools = [];
        let filteredPools = [];
        let filteredDistributions = [];
        let customNotebooks = [];

        if (pools && sourceValue) {
            const sourceObj = sources.find(x => x.hierarchy === sourceValue);
            if (sourceObj) {
                childPools = pools.filter(c => c.sources.some(cs => cs.source === sourceObj.id));
                sources.forEach(function (source) {
                    if (sourceValue === source.hierarchy) {
                        parentNode = sourceValue.replace(source.title, '');
                        parentNode = parentNode.substring(0, parentNode.length - 1);
                    }
                });
                if (parentNode !== "Global") {
                    parentPools = pools.filter(p => p.sources.some(ps => ps.source === sourceObj.id));
                }
                filteredPools = parentPools.concat(childPools).filter((elem, index, self) => index === self.indexOf(elem));
            }
        }

        if (filteredPools && poolCodeValue && distributions) {
            filteredDistributions = distributions.filter(d => d.distributionStatus !== 'Closed' && d.distributionPools && d.distributionPools.some(dp => dp.poolCode === poolCodeValue));
        }

        if (sourceValue) {
            customNotebooks = sources
                .filter(s => s.hierarchy === sourceValue)
                .flatMap(s => (s.customNotebooks || []).filter(notebook => notebook !== null && notebook !== ""));
        }

        return { filteredPools, filteredDistributions, customNotebooks };
    }

    const columns = useMemo(() => {
        const {
            storagePathItemData,
            selectedFileSystem,
            distributions,
            pools,
            formats,
            sources,
            destinations,
            containerDetailsWindowComponentData,
            fileMetadataChanged,
            displayAttachmentType,
            displayRemoveColumn,
            attchmentTypeLookupOptions,
            showSearchModal
        } = props;

        const dir: string = selectedFileSystem.fullName;
        const fields: IComponentField[] = [
            ...storagePathItemData.fields.filter(f => f.componentInstance === dir),
            ...containerDetailsWindowComponentData.fields.filter(f =>
                ["FilenameTableHeader", "LastModifiedTableHeader", "TrackingInformationTableHeader"].includes(f.name)
            )
        ];

        let orderedFields = ComponentsHelper.orderFieldsBySpecificField(fields, "order");
        if (!orderedFields || orderedFields.length === 0) {
            orderedFields = fields;
        }

        const fieldNamesToMove = ["LastModifiedTableHeader", "TrackingInformationTableHeader"];
        fieldNamesToMove.forEach(name => {
            const fieldIndex = orderedFields.findIndex(field => field.name === name);
            if (fieldIndex > -1) {
                const field = orderedFields.splice(fieldIndex, 1)[0];
                orderedFields.push(field);
            }
        });

        if (displayAttachmentType)
            orderedFields.push(containerDetailsWindowComponentData.fields.filter(f => f.name === ATTACHMENT_TYPE_TABLE_HEADER_FIELD)[0]);

        if (displayRemoveColumn)
            orderedFields.push(containerDetailsWindowComponentData.fields.filter(f => f.name === REMOVE)[0]);

        const dynamicStringSorter = (fieldName: string) => (a, b) => (a[fieldName] || '').localeCompare(b[fieldName] || '');

        const fieldsToRender = orderedFields.map(field => {
            switch (field.name) {
                case 'FilenameTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        sorter: dynamicStringSorter(field.name),
                        key: field.name,
                        ...getColumnSearchProps(field.name as string, field.data as string),
                    };
                case 'source':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: sources.map(s => ({ text: s.hierarchy, value: s.id })),
                        filterSearch: true,
                        onFilter: (value, record) => {
                            const sourceObj = sources.find(s => s.id.toString() === value.toString());
                            if (!sourceObj) return false;
                            return record?.source === sourceObj.hierarchy;
                        },
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                let value = record.source || '';
                                if (value && sources && sources.length > 0) {
                                    const found = sources.find(s => s.id === value || s.id?.toString() === value?.toString());
                                    if (found) {
                                        value = found.hierarchy;
                                    }
                                }
                                return (
                                    <DropdownTreeDataInput
                                        options={sources}
                                        changeData={(selectedValue, fieldName) => {
                                            let hierarchy = selectedValue;
                                            if (selectedValue && sources && sources.length > 0) {
                                                let found = null;
                                                if (typeof selectedValue === 'number') {
                                                    found = sources.find(s => typeof s.id === 'number' && s.id === selectedValue);
                                                } else if (typeof selectedValue === 'string' && !sources.some(s => s.hierarchy === selectedValue)) {
                                                    found = sources.find(s => s.id?.toString() === selectedValue);
                                                }
                                                if (found) {
                                                    hierarchy = found.hierarchy;
                                                } else if (typeof selectedValue === 'string' && sources.some(s => s.hierarchy === selectedValue)) {
                                                    hierarchy = selectedValue;
                                                }
                                            }
                                            fileMetadataChanged(hierarchy, fieldName, record.key, true)
                                        }}
                                        value={value}
                                        fieldName={field.name}
                                        allowNull={true}
                                        isHidden={false}
                                    />
                                );
                            } else {
                                return text || '';
                            }
                        }
                    };
                case 'distributionCode':
                    return {
                        render: (text, record, index) => {
                            const { filteredDistributions } = getFilteredData(record, pools, sources, distributions);
                            let options = (filteredDistributions || []).map(dist => ({ code: dist.distributionCode, description: dist.distributionCode }));
                            if (record.distributionCode && !options.some(opt => opt.code === record.distributionCode)) {
                                options = [{ code: record.distributionCode, description: record.distributionCode }, ...options];
                            }
                            if (record.isEditing) {
                                return renderDropdownInput(options, record.distributionCode || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        },
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: distributions.map(d => ({ text: d.distributionCode, value: d.distributionCode })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.distributionCode?.includes(value?.toString()),
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'poolCode':
                    return {
                        render: (text, record, index) => {
                            const { filteredPools } = getFilteredData(record, pools, sources, distributions);
                            let options = (filteredPools || []).map(pool => ({ code: pool.poolCode, description: pool.poolCode }));
                            if (record.poolCode && !options.some(opt => opt.code === record.poolCode)) {
                                options = [{ code: record.poolCode, description: record.poolCode }, ...options];
                            }
                            if (record.isEditing) {
                                return renderDropdownInput(options, record.poolCode || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        },
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: pools.map(p => ({ text: p.poolCode, value: p.poolCode })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.poolCode && record?.poolCode?.includes(value.toString()),
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'format': {
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: formats.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.format?.includes(value?.toString()),
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                let customNotebooks = [];
                                if (sources && record && record.source) {
                                    let sourceValue = record.source;
                                    const found = sources.find(s => s.id === sourceValue || s.id?.toString() === sourceValue?.toString());
                                    if (found) {
                                        sourceValue = found.hierarchy;
                                    }
                                    const sourceObj = sources.find(s => s.hierarchy === sourceValue);
                                    if (sourceObj && sourceObj.customNotebooks) {
                                        customNotebooks = sourceObj.customNotebooks.filter(notebook => notebook);
                                    }
                                }
                                let formatsFiltered = formats.filter(format =>
                                    format.extraFields == null || (
                                        format.extraFields["DataType"].split(',').some(f => dir.indexOf(f) >= 0) &&
                                        format.extraFields["InOut"] === "input"
                                    )
                                );
                                if (customNotebooks.length > 0) {
                                    customNotebooks.forEach(customNotebook => {
                                        formatsFiltered.push({
                                            typeID: 0,
                                            code: `${customNotebook}.ipynb`,
                                            description: `${customNotebook}.ipynb`
                                        });
                                    });
                                }
                                const options = formatsFiltered.map((f) => ({ code: f.description, description: f.description }));
                                return renderDropdownInput(options, record.format || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        }
                    };
                }
                case "outputFormat": {
                    let formatsFiltered = formats.filter(format =>
                        format.extraFields == null ||
                        (format.extraFields["DataType"].split(',').some(f => dir.indexOf(f) >= 0) &&
                            format.extraFields["InOut"] === "output")
                    );
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: formatsFiltered.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.outputFormat?.includes(value.toString()),
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                const options = formatsFiltered.map((f) => ({ code: f.description, description: f.description }));
                                return renderDropdownInput(options, record.outputFormat || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        }
                    };
                }
                case 'destination':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: destinations.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => {
                            return record?.destination?.includes(value?.toString());
                        },
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                const options = destinations.map((d) => ({ code: d.description, description: d.description }));
                                return renderDropdownInput(options, record.destination || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        }
                    };
                case 'LastModifiedTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'TrackingInformationTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'batchID':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                };
                case 'fileSize':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: (a, b) => {
                            const aSize = typeof a.fileSize === 'number' ? a.fileSize : 0;
                            const bSize = typeof b.fileSize === 'number' ? b.fileSize : 0;
                            return aSize - bSize;
                        },
                        render: (text, record) =>
                            typeof record.fileSize === 'number'
                                ? formatFileSize(record.fileSize)
                                : '',
                    };
                case 'AttachmentTypeTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                        render: (_text, record, index) => {
                            const options = attchmentTypeLookupOptions.map((d) => ({ code: d.code, description: d.description }));
                            return renderDropdownInput(
                                options,
                                record?.attachmentTypeCode || '',
                                field.name,
                                record,
                                fileMetadataChanged,
                                index
                            );
                        }
                    };
                case 'remove_action':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        render: (_text, record) => {
                                return (
                                    <IconTextButton
                                        onClick={() => onRemoveFile(record)}
                                        icon={DELETE_ICON}
                                    />
                                );
                        }
                    };
                case 'rightHolder':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        render: (record) => renderTextInputWithShowMore(record, field, showSearchModal, 'SelectIP', 'CONTRIBUTORS_KEY', fileMetadataChanged)
                    };
                case 'shoppingCart':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        render: (record) => renderTextInputWithShowMore(record, field, showSearchModal, 'Select Shopping Cart', SEARCH_VIEW_LICENSES_FILEIMPORT, fileMetadataChanged)
                    };
                default:
                    return {};
            }
        });
        return [getActionIconsColumn(), ...fieldsToRender, getDynamicActionColumn()];
    }, [props]);

    const data = useMemo(() => {
        const storagePaths = props.storagePaths;
        if (storagePaths.length !== 0) {
            for (var storageItem of storagePaths) {
                if (storageItem.metadata != null) {
                    for (var job of props.jobs) {
                        if (storageItem.metadata?.jobID == job.jobID) {
                            storageItem.inProgress = true;
                        }
                    }
                }
            }
        }
        return storagePaths.map(row => ({
            key: row?.fullName,
            FilenameTableHeader: row?.name,
            distributionCode: row?.metadata?.distributionCode,
            poolCode: row?.metadata?.poolCode,
            format: row?.metadata?.format,
            outputFormat: row?.metadata?.outputFormat,
            source: row?.metadata?.source,
            destination: row?.metadata?.destination,
            fileSize: row?.metadata?.fileSize,
            LastModifiedTableHeader: row?.lastModified,
            TrackingInformationTableHeader: row?.metadata?.trackingInformation,
            inProgress: row?.inProgress,
            isEditing: row?.isEditing,
            isDirectory: row?.isDirectory,
            isDirty: row?.isDirty,
            attachmentTypeCode: row?.attachmentTypeCode,
            attachmentTypeID: row?.attachmentTypeID,
            rightHolder: row?.metadata?.rightHolder,
            batchID: row?.metadata?.batchID,
            validationError: row?.validationError,
        }));
    }, [props.storagePaths, props.jobs]);

    const onRowClick = (record: IDataIngestionTableRow) => {
        const { openDirectory, sources } = props;
        var fullName = record?.key;
        var name = record?.FilenameTableHeader
        if (record?.isDirectory) {
            openDirectory({
                fullName,
                name,
                fileSystems: undefined,
                canEditMetadata: undefined,
                hasMetadata: undefined,
                jobTypes: undefined,
                parent: undefined,
                path: fullName
            },
                sources);
        }
    }
    
    const expandedRowKeys = useMemo(() => data.filter(row => row.validationError).map(row => row.key), [data]);

    return (
        <div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100, 250, 500, 1000],
                    position: ["bottomRight", "topRight"],
                    showTotal: (total) => (
                        <b>{total} Results</b>
                    ),
                }}
                onRow={(record) => ({
                    onClick: () => onRowClick(record)
                })}
                size={'middle'}
                className={"parent-table"}
                expandable={{
                    expandedRowRender: (record) =>
                        record.validationError ? (
                            <div className="text-danger">{record.validationError}</div>
                        ) : null,
                    expandedRowKeys: expandedRowKeys,
                    expandIconColumnIndex: -1,
                    expandIcon: () => null
                }}
            />
        </div>
    );
};

export default DataIngestionTable;
